import * as Tabs from "@radix-ui/react-tabs";

import { Skeleton } from "~/components/ui/skeleton";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";

/**
 * Skeleton for the PDP tabs section. Mirrors the tab strip + 234px-tall
 * content area used by the real `<PDPTabs />` so the section doesn't
 * reflow when the tabs content resolves.
 */
export const PDPTabsSkeleton = ({ tabs = 3 }: { tabs?: number } = {}) => (
  <div className="product-tabs flex w-full flex-col items-center pb-[56px]">
    <div className="w-full px-[40px]">
      <div className="flex items-center justify-center gap-6 border-b border-b-gray-200">
        {Array.from({ length: tabs }).map((_, i) => (
          <Skeleton key={i} className="my-[7px] h-6 w-24" />
        ))}
      </div>
    </div>
    <div className="flex h-[234px] items-start justify-center self-stretch px-[40px] py-[24px] sm-max:h-auto">
      <div className="w-full max-w-3xl space-y-2">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  </div>
);

const PDPTabs = ({ content }: { content: SerializeFrom<any[]> }) => {
  const fields = content.map(tab => tab.fields);
  const defaultTab = fields.find(f => f.showTabs);

  const getEntryId = (fieldId: string) => {
    return content.find(e => e.fields.id === fieldId)?.sys.id;
  };

  return (
    <Tabs.Root
      className="product-tabs flex w-full flex-col items-center pb-[56px]"
      defaultValue={defaultTab?.id}
    >
      <Tabs.List className="w-full px-[40px]">
        <div
          className={cn(
            "flex items-center justify-center border-b border-b-gray-200",
            !defaultTab && "hidden",
          )}
        >
          {fields.map(
            f =>
              f.showTabs && (
                <span key={f.id}>
                  <Tabs.Trigger
                    className="data-[state=active]:decoration-accent-10 flex h-[40px] cursor-default select-none items-center justify-center bg-transparent px-[16px] text-lg font-bold leading-[26px] text-neutral-12 outline-none data-[state=inactive]:text-neutralalpha-11 data-[state=active]:underline data-[state=active]:underline-offset-[11px]"
                    value={f.id}
                    data-testid={`pdp-tab-${f.id}`}
                  >
                    {f.tabTitle}
                  </Tabs.Trigger>
                </span>
              ),
          )}
        </div>
      </Tabs.List>
      {fields.map(
        f =>
          f.showTabs && (
            <span key={f.id}>
              <Tabs.Content
                className="flex h-[234px] items-start justify-center gap-[24px] self-stretch bg-transparent px-[40px] data-[state=inactive]:hidden sm-max:h-auto"
                value={f.id}
                data-testid={`pdp-tab-content-${f.id}`}
              >
                <div className="text-lg leading-[26px]">
                  <p className="py-[24px] font-bold text-neutral-12">
                    {f.tabContentTitle}
                  </p>
                  <div className="columns-1 gap-[24px] font-light text-neutralalpha-11 sm:columns-2 lg:columns-3">
                    {/* TODO: Replace with Builder.io component (RichTextRender) */}
                  </div>
                </div>
              </Tabs.Content>
            </span>
          ),
      )}
    </Tabs.Root>
  );
};

export { PDPTabs };
